import { useNavigate } from "react-router-dom";
import { Mic, ChevronDown, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useMemo } from "react";
import { useVoiceAssistant } from "@/hooks/useVoiceAssistant";

import { getWhatsAppLink } from "@/config/company";
import { AIChatDialog } from "@/components/ai/AIChatDialog";
import { HeroCombobox } from "@/components/home/HeroCombobox";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

// Static data - updated at build time by prerender script
import { 
  HERO_CITIES, 
  HERO_CATEGORIES, 
  HERO_TREATMENTS, 
  getFilteredTreatments, 
  findTreatmentBySlug 
} from "@/data/heroData";

export function HeroSection() {
  const navigate = useNavigate();
  const [location, setLocation] = useState("any");
  const [category, setCategory] = useState("any");
  const [subCategory, setSubCategory] = useState("any");
  
  // All options are static - no API calls needed
  const treatmentOptions = useMemo(() => getFilteredTreatments(category), [category]);

  // Reset subCategory when category changes
  useEffect(() => {
    setSubCategory("any");
  }, [category]);

  const { isConnecting, isActive, toggleConversation, assistantState } = useVoiceAssistant();
  const { trackCTAClick, trackWhatsAppClick, trackSearch } = useAnalyticsContext();

  const handleExplore = () => {
    if (isActive) return;
    
    const hasLocation = location !== "any";
    const hasCategory = category !== "any" && category !== "__explore__";
    const hasTreatment = subCategory !== "any" && subCategory !== "__explore__";
    
    // Priority 1: If a specific treatment is selected, navigate to treatment detail
    if (hasTreatment) {
      const selectedTreatment = findTreatmentBySlug(subCategory);
      if (selectedTreatment) {
        navigate(`/treatments/${selectedTreatment.category}/${selectedTreatment.slug}/`);
        return;
      }
    }
    
    // Priority 2: If category is selected (with or without location), show treatments in that category
    if (hasCategory) {
      navigate(`/treatments/${category}/`);
      return;
    }
    
    // Priority 3: If only location is selected, show hospitals in that city
    if (hasLocation) {
      navigate(`/hospitals/?city=${location}`);
      return;
    }
    
    // Default: Navigate to all treatments
    navigate(`/treatments/`);
  };

  // Tracked explore handler
  const handleTrackedExplore = () => {
    trackCTAClick('Explore', 'hero_search', 'primary');
    if (subCategory !== 'any') {
      trackSearch(subCategory);
    }
    handleExplore();
  };

  // Handle "Explore" option selections - navigate to treatments page
  const handleCategoryChange = (value: string) => {
    if (value === "__explore__") {
      navigate("/treatments/");
      return;
    }
    setCategory(value);
  };

  const handleSubCategoryChange = (value: string) => {
    if (value === "__explore__") {
      navigate("/treatments/");
      return;
    }
    setSubCategory(value);
  };

  const scrollToContent = () => {
    if (isActive) return;
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[100dvh] flex flex-col items-center justify-center overflow-hidden py-16 md:py-0">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 z-0 text-balance">
        <img
          src="/hero-medical.webp"
          alt="World-class medical facility in India"
          width={1920}
          height={1080}
          className="w-full h-full object-cover object-center"
          loading="eager"
          decoding="async"
          {...{ fetchpriority: "high" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/85 via-secondary/75 to-secondary/95" />
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 relative z-10 text-center flex flex-col items-center pt-20">
      {/* Hidden SEO content - visually hidden but crawlable for featured snippets */}
        <span className="sr-only">
          What is Medical Tourism? Medical tourism is when patients travel internationally 
          to receive medical treatment at a lower cost or higher quality than available 
          in their home country. India is a leading destination offering world-class 
          healthcare at 30-70% savings compared to Western countries.
        </span>

        {/* Visible Headline */}
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-4 md:mb-6 tracking-tight drop-shadow-md">
          Discover a World of Healing
          <br />
          <span className="text-accent italic">Beyond Borders</span>
        </h1>

        {/* Visible Description */}
        <p className="mb-6 md:mb-8 max-w-2xl mx-auto">
          <span className="block text-sm sm:text-base md:text-lg font-medium text-primary-foreground leading-relaxed mb-1">
            Access World-Class Healthcare and Expert Doctors at Affordable Prices.
          </span>
          <span className="block text-xs sm:text-sm md:text-base text-primary-foreground/85">
            Save up to 70% at top JCI-accredited hospitals in India.
          </span>
        </p>

        {/* Search Filters - Mobile optimized with Floating Effect */}
        <div className="bg-background/80 backdrop-blur-2xl border border-white/20 rounded-2xl md:rounded-full p-3 md:p-1.5 shadow-2xl w-full max-w-3xl mx-auto mb-8 md:mb-10 animate-slide-up hover:shadow-primary/5 transition-all duration-300" style={{ animationDelay: "0.3s" }}>
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-1.5">
            <HeroCombobox
              value={location}
              onValueChange={setLocation}
              placeholder="Any Location"
              ariaLabel="Select Location"
              options={HERO_CITIES}
              triggerClassName="w-full md:flex-1 border-0 bg-transparent h-11 text-sm text-foreground font-medium focus:ring-0 rounded-xl md:rounded-full px-4 hover:bg-muted/50 transition-colors"
            />

            <div className="hidden md:block w-px h-6 bg-border/50" />

            <HeroCombobox
              value={category}
              onValueChange={handleCategoryChange}
              placeholder="Choose Category"
              ariaLabel="Select Category"
              options={HERO_CATEGORIES}
              triggerClassName="w-full md:flex-1 border-0 bg-transparent h-11 text-sm text-foreground font-medium focus:ring-0 rounded-xl md:rounded-full px-4 hover:bg-muted/50 transition-colors"
            />

            <div className="hidden md:block w-px h-6 bg-border/50" />

            <HeroCombobox
              value={subCategory}
              onValueChange={handleSubCategoryChange}
              placeholder="All Treatments"
              ariaLabel="Select Treatment"
              disabled={false}
              options={treatmentOptions}
              triggerClassName="w-full md:flex-[1.2] border-0 bg-transparent h-11 text-sm text-foreground font-medium focus:ring-0 rounded-xl md:rounded-full px-4 hover:bg-muted/50 transition-colors"
            />

            <Button 
              onClick={handleTrackedExplore}
              className="w-full md:w-auto rounded-xl md:rounded-full px-6 h-11 text-sm font-semibold shadow-lg shadow-accent/20 hover:shadow-accent/40 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 mt-1.5 md:mt-0"
            >
              Explore
            </Button>
          </div>
        </div>

        {/* CTA Buttons - Voice + Chat + WhatsApp */}
        <div className="flex flex-col items-center gap-4 animate-slide-up" style={{ animationDelay: "0.4s" }}>
          <div className="flex flex-row flex-wrap items-center justify-center gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                trackCTAClick('Speak with AI', 'hero_cta', 'voice');
                toggleConversation();
              }}
              disabled={isConnecting || isActive}
              className="rounded-full border border-accent/40 bg-accent/5 hover:bg-accent/20 text-primary-foreground gap-2 text-[10px] sm:text-xs md:text-sm h-9 sm:h-10"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Calling...
                </>
              ) : isActive ? (
                <>
                  <Mic className="w-3.5 h-3.5 text-accent animate-pulse" />
                  In Call...
                </>
              ) : (
                <>
                  <Mic className="w-3.5 h-3.5 text-accent" />
                  Speak with AI
                </>
              )}
            </Button>
            <AIChatDialog
              context="Homepage visitor exploring medical treatment options in India"
              className="rounded-full border border-accent/40 bg-accent/5 hover:bg-accent/20 text-primary-foreground gap-2 text-[10px] sm:text-xs md:text-sm h-9 sm:h-10"
            />
            <Button 
              asChild
              variant="outline"
              className="rounded-full border border-accent/40 bg-accent/5 hover:bg-accent/20 text-primary-foreground gap-2 text-[10px] sm:text-xs md:text-sm h-9 sm:h-10"
              onClick={() => trackWhatsAppClick('hero')}
            >
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-3.5 h-3.5 text-accent" />
                WhatsApp
              </a>
            </Button>
          </div>
          <p className="text-primary-foreground/60 text-[10px] sm:text-xs font-medium">
            Get instant answers about treatments & pricing
          </p>
        </div>
      </div>

      {/* Explore Below Indicator - Hidden on very short screens if necessary, otherwise adjusted */}
      <div 
        className="absolute bottom-4 sm:bottom-6 left-0 right-0 z-10 hidden sm:flex justify-center cursor-pointer animate-float"
        onClick={scrollToContent}
        role="button"
        tabIndex={0}
        aria-label="Scroll to content"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scrollToContent();
          }
        }}
      >
        <div className="flex flex-col items-center text-primary-foreground/40 hover:text-primary-foreground/70 transition-colors">
          <span className="text-[10px] font-bold tracking-widest uppercase mb-1">Explore</span>
          <div className="w-6 h-6 rounded-full border border-primary-foreground/20 flex items-center justify-center">
            <ChevronDown className="w-3 h-3" />
          </div>
        </div>
      </div>
    </section>
  );
}
